import { TriStateEnum } from "@/app/entities/TriState";
import { useAuth } from "@/app/hooks/useAuth";
import { configService } from "@/app/services/configService";
import { UpdateConfigProductRequest } from "@/app/services/configService/updateConfigProduct";
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners";
import { PartnerType, getPartnerLabel } from "@/app/utils/partners.enum";
import { useToast } from "@/view/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

const imageField = z.custom<File | string>(
  (val): val is File | string => {
    if (val instanceof File) return val.size > 0;
    if (typeof val === "string") return val.trim().length > 0;
    return false;
  },
  { message: "Campo obrigatório" },
);

const schema = z.object({
  label: z.string().min(1, "Campo obrigatório"),
  subtitle: z.string().min(1, "Campo obrigatório"),
  image: imageField,
});

type IProduct = {
  name: PartnerType;
  label?: string;
  itens: any[];
  tableId?: string;
  delay?: number;
  isActive?: boolean;
  state?: TriStateEnum;
  image?: string;
  subtitle?: string;
  color?: string;
  createLink?: boolean;
};
export type FormSchema = z.infer<typeof schema>;

const isNewImageFile = (file?: File): file is File => !!file && file.size > 0;

export const useConfigPartner = (product: IProduct) => {
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAuth();

  const [isColorModalOpen, setIsColorModalOpen] = useState(false);
  // Usa por padrão a cor que vem do código primeiro.
  const [partnerColor, setPartnerColor] = useState(
    getPartnerBgColor(product?.name as PartnerType) ?? product?.color,
  );

  const [createLink, setCreateLink] = useState(false);
  const [productState, setProductState] = useState<TriStateEnum>(
    product?.state ?? TriStateEnum.OFF,
  );

  const { toast } = useToast();

  // Usa por padrão título, descrição e imagem que vêm do código primeiro.
  const form = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      label: getPartnerLabel(product?.name as PartnerType) ?? product?.label,
      subtitle:
        getPartnerLabel(product?.name as PartnerType) ??
        product?.subtitle ??
        product?.name ??
        "",
      image: getPartnerImage(product?.name as PartnerType) ?? product?.image,
    },
  });

  const {
    mutateAsync: uploadProductImage,
    isPending: isUploadingProductImage,
  } = useMutation({
    mutationFn: async (image: File) => {
      return configService.uploadProductImage(
        product?.name as PartnerType,
        image,
      );
    },
  });

  const { mutateAsync: updateProduct, isPending: isUpdatingProduct } =
    useMutation({
      mutationFn: async (input: UpdateConfigProductRequest) => {
        return configService.updateConfigProduct(input);
      },
    });

  const { mutateAsync: patchProductState, isPending: isPatchingState } =
    useMutation({
      mutationFn: async (state: TriStateEnum) => {
        return configService.patchConfigProducts({
          type: product?.name as PartnerType,
          state,
          isActive: product?.isActive ?? false,
        });
      },
    });

  const handleProductStateChange = async (state: TriStateEnum) => {
    const previousState = productState;
    setProductState(state);
    try {
      await patchProductState(state);
      queryClient.invalidateQueries({ queryKey: ["productsConfig"] });
    } catch (error) {
      setProductState(previousState);
      toast({
        title: "Erro ao atualizar estado do produto",
        variant: "destructive",
      });
    }
  };

  const { mutateAsync: patchCreateLink, isPending: isPatchingCreateLink } =
    useMutation({
      mutationFn: async (createLinkValue: boolean) => {
        return configService.updateConfigProduct({
          type: product?.name as PartnerType,
          createLink: createLinkValue,
        });
      },
    });

  const handleCreateLinkChange = async (value: boolean) => {
    const previousValue = createLink;
    setCreateLink(value);
    try {
      await patchCreateLink(value);
      queryClient.invalidateQueries({ queryKey: ["productsConfig"] });
    } catch (error) {
      setCreateLink(previousValue);
      toast({
        title: "Erro ao atualizar link de autocontração",
        variant: "destructive",
      });
    }
  };

  const handleUpdateProduct: SubmitHandler<FormSchema> = async (data) => {
    const type = product?.name as PartnerType;

    const payload = {
      type,
      createLink,
      subtitle: data.subtitle,
      label: data.label,
      color: partnerColor,
    };
    try {
      if (data.image instanceof File && isNewImageFile(data.image)) {
        await uploadProductImage(data.image);
      }
      await updateProduct(payload);
      toast({ title: "Configurações salvas com sucesso" });
      queryClient.invalidateQueries({ queryKey: ["productsConfig"] });
    } catch (error) {
      toast({ title: "Erro ao salvar configurações", variant: "destructive" });
    }
  };

  const handleRemoveImage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.setValue("image", "", {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    if (product) {
      // Usa por padrão os valores do código primeiro.
      form.reset({
        label: getPartnerLabel(product.name) ?? String(product.name),
        subtitle: getPartnerLabel(product.name) ?? product.subtitle ?? "",
        image: getPartnerImage(product.name) ?? product.image ?? "",
      });

      setPartnerColor(getPartnerBgColor(product.name) ?? product.color);
      if (product.createLink) setCreateLink(product.createLink);
      setProductState(product.state ?? TriStateEnum.OFF);
    }
  }, [product]);

  const isSaving = isUpdatingProduct || isUploadingProductImage;

  return {
    form,
    isColorModalOpen,
    setIsColorModalOpen,
    partnerColor,
    setPartnerColor,
    isSaving,
    createLink,
    handleUpdateProduct,
    handleRemoveImage,
    setCreateLink,
    productState,
    handleProductStateChange,
    isPatchingState,
    isSuperAdmin,
    handleCreateLinkChange,
    isPatchingCreateLink,
  };
};
